import React, { useEffect, useMemo, useState } from 'react';
import {
  OVERVIEW_TEMPLATE_STRINGS,
  auditBriefNonAdvisory,
  buildOverviewBrief,
} from '../../utils/overviewBrief';
import { hashWithContext, sourceTabForItem } from '../../utils/selectionContext';
import { track } from '../../lib/track';
import { useDeal } from '../../deals/DealContext';
import { dealDataUrl } from '../../deals/registry';
import { useAuth } from '../../auth/AuthContext';
import { useAssistantPageExtras } from '../assistant/AssistantContext';
import ValueRisks from '../../screen/ValueRisks.jsx';

const [TITLE, SUBTITLE, LOOKS, CONCERNING, UNKNOWN, CITE_HINT, RULE_HINT, LIMITED_HINT] =
  OVERVIEW_TEMPLATE_STRINGS;

function BriefSection({ title, tone, items, onOpen }) {
  return (
    <section className={`dashboard-section overview-section overview-section-${tone}`}>
      <div className="section-title">{title}</div>
      {!items.length ? (
        <p className="overview-empty">None flagged from current inputs.</p>
      ) : (
        <div className="overview-list">
          {items.map((item) => (
            <article key={item.id || item.title} className={`overview-card overview-card-${tone}`}>
              <div className="overview-card-head">
                <h3 className="overview-card-title">{item.title}</h3>
                {item.severity ? (
                  <span className={`pill severity-${String(item.severity).toLowerCase()}`}>
                    {item.severity}
                  </span>
                ) : null}
              </div>
              {item.text ? <p className="overview-card-text">{item.text}</p> : null}
              {item.citations?.length ? (
                <ul className="overview-citations">
                  {item.citations.map((c) => (
                    <li key={c.key || c.label}>
                      <code>{c.key || c.label}</code>
                      {c.label && c.label !== c.key ? <span> · {c.label}</span> : null}
                    </li>
                  ))}
                </ul>
              ) : null}
              {onOpen ? (
                <button
                  type="button"
                  className="overview-jump"
                  onClick={() => onOpen(item)}
                >
                  {OVERVIEW_TEMPLATE_STRINGS[8]}
                </button>
              ) : null}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

export default function Overview({
  dealPackage = null,
  diligence = null,
  executive = null,
  onNavigate,
}) {
  const [packageBrief, setPackageBrief] = useState(null);
  const [briefStatus, setBriefStatus] = useState('loading');
  const { deal } = useDeal();
  const auth = useAuth();
  const { setPageExtras, clearPageExtras } = useAssistantPageExtras();
  const onScreenContext = React.useCallback(
    (ctx) => {
      if (ctx) setPageExtras({ tab: 'overview', valueRisks: ctx });
    },
    [setPageExtras],
  );
  useEffect(() => () => clearPageExtras(), [clearPageExtras]);

  useEffect(() => {
    let cancelled = false;
    setBriefStatus('loading');
    // Optional artifact — 404/empty soft-fails to rule-based brief (no paging).
    fetch(dealDataUrl(deal, 'package-brief.json', import.meta.env.BASE_URL), {
      credentials: 'include',
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled) return;
        setPackageBrief(data);
        setBriefStatus(data ? 'file' : 'absent');
      })
      .catch(() => {
        if (!cancelled) {
          setPackageBrief(null);
          setBriefStatus('absent');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [deal]);

  const brief = useMemo(
    () =>
      buildOverviewBrief({
        packageBrief,
        dealPackage,
        diligence,
        executive,
      }),
    [packageBrief, dealPackage, diligence, executive],
  );

  const audit = useMemo(() => auditBriefNonAdvisory(brief), [brief]);
  const briefReady = briefStatus !== 'loading';

  const handleOpen = (item) => {
    const tab = sourceTabForItem(item);
    const selection = {
      concernId: item.id || item.title || '',
      selectedWellId: item.wellId || '',
      selectedMonth: '',
      scenarioId: '',
    };
    track('overview_concern_opened', {
      concern_id: selection.concernId.slice(0, 80),
      source_tab: tab,
      kind: item.kind || undefined,
    });
    const hash = hashWithContext(tab, { selection });
    if (typeof onNavigate === 'function') {
      onNavigate(tab, { selection, hash });
    } else {
      window.location.hash = hash;
    }
  };

  return (
    <div className="dashboard-page overview-page">
      <div className="page-header">
        <div className="page-header-left">
          <h2 className="page-title">{TITLE}</h2>
          <p className="page-subtitle">{SUBTITLE}</p>
          <p className="overview-hint">
            {brief.source === 'rule-based' ? RULE_HINT : CITE_HINT}
            {!dealPackage ? ` ${LIMITED_HINT}` : ''}
          </p>
          <div className="overview-meta">
            <span className="tag">{brief.source}</span>
            {brief.meta?.dealId ? <span className="tag">{brief.meta.dealId}</span> : null}
            {brief.meta?.asOf ? <span className="tag">as of {brief.meta.asOf}</span> : null}
            {briefStatus === 'file' ? <span className="tag tag-model">package-brief.json</span> : null}
          </div>
        </div>
      </div>

      {/* Screen result first (Luther): buy-box outcome, value bridge, risks; links to dashboard. */}
      {auth.isInternal && !deal?.draft ? (
        <ValueRisks key={deal?.dealId} dealId={deal?.dealId} onContext={onScreenContext} />
      ) : null}

      {!briefReady ? (
        <div className="loading-screen">
          <div className="loading-spinner" />
          <p>Loading overview brief…</p>
        </div>
      ) : !audit.ok ? (
        <div className="error-screen overview-audit-fail">
          <h2>Brief blocked</h2>
          <p>Overview copy failed the non-advisory language audit.</p>
        </div>
      ) : (
        <>
          <BriefSection title={LOOKS} tone="strength" items={brief.strengths} />
          <BriefSection
            title={CONCERNING}
            tone="concern"
            items={brief.concerns}
            onOpen={handleOpen}
          />
          <BriefSection
            title={UNKNOWN}
            tone="unknown"
            items={brief.unknowns}
            onOpen={handleOpen}
          />
        </>
      )}

      {briefReady ? <p className="engine-note overview-disclaimer">{brief.meta?.disclaimer}</p> : null}
    </div>
  );
}
